import React, { memo, useRef, useCallback } from 'react'
import FlashIcon from '../../assets/icons/Flash'
import { useSelector } from 'react-redux'
import { Box, MenuItem, Tooltip, Typography } from '@mui/material'
import { AppMenu } from '../common/AppMenu'
import { selectMultiPaneFullScreen } from '../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import QuickWidgetMenuItem from './quickWidgetMenuItem';
import { QuickWidgetTypes } from '../../reducer/user.reducer'
import { ExtraSmallButton, NonInteractiveTooltip } from '../custom'
import { mergeSideBarBottomButtonSx } from '../sideBarRight/sideBarBottomIconButtonSx'
import ToggleSwitch from '../common/toggleSwitch/toggleSwitch'
import { useQuickWidgetActions } from '../../hooks/useQuickWidgetActions'

const QuickWidgetsMenu: React.FC = () => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const container = useSelector(selectMultiPaneFullScreen)
    const open = Boolean(anchorEl)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const { widgetsVisible, widgetActive, handleToggle } = useQuickWidgetActions()

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const handleButtonClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        if (open) {
            handleClose()
        } else {
            setAnchorEl(event.currentTarget)
        }
    }, [open, handleClose])

    return (
        <div>
            <Tooltip title={<span style={{ fontSize: '12px' }}>Quick Widgets</span>} arrow placement="left">
                <span>
                    <ExtraSmallButton
                        ref={buttonRef}
                        aria-label="Quick Widgets"
                        size="small"
                        color="inherit"
                        onClick={handleButtonClick}
                        data-event-source="right-sidebar"
                        data-event={'Quick Widgets Menu Opened'}
                        sx={mergeSideBarBottomButtonSx(theme => ({
                            backgroundColor: open ? theme.palette.action.selected : 'transparent'
                        }))}
                    >
                        <FlashIcon />
                    </ExtraSmallButton>
                </span>
            </Tooltip>

            <AppMenu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                clickAwayIgnoreRefs={[buttonRef]}
                container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                transitionDuration={0}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <MenuItem>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography component="span" sx={{ lineHeight: '24px', backgroundColor: 'unset', fontSize: 12, opacity: 0.5, paddingLeft: 0 }}>
                            QUICK WIDGETS
                        </Typography>
                        <NonInteractiveTooltip title="⇧ + Q" arrow placement="top">
                            <ToggleSwitch
                                size="small"
                                checked={widgetsVisible}
                                disableRipple
                                onChange={handleToggle}
                            />
                        </NonInteractiveTooltip>
                    </Box>
                </MenuItem>

                <QuickWidgetMenuItem widgetType={QuickWidgetTypes.MARKET_DEPTH} leftPadding={widgetActive} text="Market Depth" closeMenu={handleClose} disabled={!widgetsVisible} shortcutHint="D" />
                <QuickWidgetMenuItem widgetType={QuickWidgetTypes.ORDER} leftPadding={widgetActive} text="Orders" closeMenu={handleClose} disabled={!widgetsVisible} shortcutHint="O" />
                <QuickWidgetMenuItem widgetType={QuickWidgetTypes.POSITION} leftPadding={widgetActive} text="Positions" closeMenu={handleClose} disabled={!widgetsVisible} shortcutHint="P" />
                <QuickWidgetMenuItem widgetType={QuickWidgetTypes.QUICK_TRADE} leftPadding={widgetActive} text="Quick Trade" closeMenu={handleClose} disabled={!widgetsVisible} shortcutHint="T" />
            </AppMenu>
        </div>
    )
}

export default memo(QuickWidgetsMenu)
